import type { ListeningOff } from "@aybuben/client/device/listening.ts";
import { useContext, useEffect, useMemo, type ReactNode } from "react";

import { setRoundMuted } from "../../listening-flag.ts";
import { SoundContext, type SoundApi } from "../sound.tsx";
import { ListeningContext, useListening, type ListeningApi } from "./context.tsx";

/** What a muted round holds on to once the device's own window has ended. */
const LAPSED: ListeningOff = { since: 0, until: 0 };

const noSound = (base: SoundApi): SoundApi => ({
	...base,
	play: () => {},
	playAuto: () => {},
});

/**
 * Everything below hears nothing while listening is off. A `round` that began
 * muted keeps it so when the window ends mid-way: it never flips back.
 */
export function ListeningScope({
	round = false,
	children,
}: {
	round?: boolean;
	children: ReactNode;
}) {
	const api = useListening();
	const base = useContext(SoundContext);
	const off = api.off ?? (round ? LAPSED : null);
	const value = useMemo<ListeningApi>(() => (off === api.off ? api : { ...api, off }), [api, off]);
	// Cues are not played through the sound context, so the round's silence is also a flag they read.
	useEffect(() => {
		setRoundMuted(round);
	}, [round]);
	useEffect(() => () => setRoundMuted(false), []);
	const sound = useMemo(() => (base && off ? noSound(base) : base), [base, off]);
	return (
		<ListeningContext.Provider value={value}>
			<SoundContext.Provider value={sound}>{children}</SoundContext.Provider>
		</ListeningContext.Provider>
	);
}
